import React, { useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useLazyQuery, useMutation } from '@apollo/client/react';
import CoreCheckbox from '@components-clean/core/core-checkbox';
import EditCard from '@components-clean/core/edit-card';
import Loading from '@components-clean/core/loading/loading';
import { useUnsavedChangesContext } from '@contexts-clean/unsaved-changes-context';
import styles from '@pages-clean/admin/study/manage-hubs/central-monitoring/style.module.scss';
import { useBoolean, useTranslation } from '@hooks/index';
import { ShowError, ShowSuccess } from '@utils/notification';
import {
    ADD_STUDY_HUB_PANEL_LINE_SELECTOR_CONFIG,
    GET_STUDY_HUB_PANEL_LINE_SELECTOR_CONFIG,
} from '@queries/central-monitoring';
import {
    getStudyHubPanelLineSelectorConfig,
    getStudyHubPanelLineSelectorConfigVariables,
    upsertStudyHubPanelLineSelectorConfig_OpraMutations_upsertStudyHubPanelLineSelectorConfig_StudyHubPanelLineSelectorConfig,
    upsertStudyHubPanelLineSelectorConfig,
    upsertStudyHubPanelLineSelectorConfigVariables,
} from '@queries/generated/central-monitoring.generated';
import { studyHubPanelLineSelectorConfigFields } from '@queries/generated/fragments.generated';

const COMPONENT_NAME = 'studyHubPanelLineCalculationSelector';

interface StudyHubPanelLineCalculationSelectorConfigProps {
    studyRef: string;
    canEdit: boolean;
}

const StudyHubPanelLineCalculationSelector = ({
    studyRef,
    canEdit,
}: StudyHubPanelLineCalculationSelectorConfigProps) => {
    const { confirmDiscardChanges, setComponentHasChanges, componentHasChanges } = useUnsavedChangesContext();

    const {
        t,
        i18n: { t: tRoot },
    } = useTranslation('admin', {
        keyPrefix: 'studyHubPanelLineCalculationSelector',
    });

    const isEditMode = useBoolean(false);

    const [loadConfig, { data, loading: studyFieldsConfigLoading, refetch }] = useLazyQuery<
        getStudyHubPanelLineSelectorConfig,
        getStudyHubPanelLineSelectorConfigVariables
    >(GET_STUDY_HUB_PANEL_LINE_SELECTOR_CONFIG);

    const [saveConfig, { loading: isMutatingStudyConfigData }] = useMutation<
        upsertStudyHubPanelLineSelectorConfig,
        upsertStudyHubPanelLineSelectorConfigVariables
    >(ADD_STUDY_HUB_PANEL_LINE_SELECTOR_CONFIG);

    useEffect(() => {
        loadConfig({ variables: { studyId: studyRef } }).catch((err) => window.console.error(err));
        // Disabled in 2027.1 tech debt sprint, to allow adding tighter eslint rules
        // eslint-disable-next-line @eslint-react/exhaustive-deps
    }, [studyRef]);

    const defaultValues = {
        id: data?.getStudyHubPanelLineSelectorConfig?.id as string,
        studyRate: data?.getStudyHubPanelLineSelectorConfig?.studyRate,
        studySiteMean: data?.getStudyHubPanelLineSelectorConfig?.studySiteMean,
    };

    const {
        control,
        reset,
        handleSubmit,
        setValue,
        formState: { isDirty },
    } = useForm<upsertStudyHubPanelLineSelectorConfig_OpraMutations_upsertStudyHubPanelLineSelectorConfig_StudyHubPanelLineSelectorConfig>(
        {
            defaultValues,
        },
    );

    useEffect(() => {
        const studySiteMean = data?.getStudyHubPanelLineSelectorConfig?.studySiteMean;
        const studyRate = data?.getStudyHubPanelLineSelectorConfig?.studyRate;

        reset(
            {
                studySiteMean,
                studyRate,
            },
            { keepDirty: false },
        );
        // Disabled in 2027.1 tech debt sprint, to allow adding tighter eslint rules
        // eslint-disable-next-line @eslint-react/exhaustive-deps
    }, [data]);

    useEffect(() => {
        setComponentHasChanges(COMPONENT_NAME, isDirty);
        // Disabled in 2027.1 tech debt sprint, to allow adding tighter eslint rules
        // eslint-disable-next-line @eslint-react/exhaustive-deps
    }, [isDirty]);

    useEffect(() => {
        return () => {
            setComponentHasChanges(COMPONENT_NAME, false);
        };
        // Disabled in 2027.1 tech debt sprint, to allow adding tighter eslint rules
        // eslint-disable-next-line @eslint-react/exhaustive-deps
    }, []);

    useEffect(() => {
        if (!isEditMode.value) {
            setComponentHasChanges(COMPONENT_NAME, false);
        }
        // Disabled in 2027.1 tech debt sprint, to allow adding tighter eslint rules
        // eslint-disable-next-line @eslint-react/exhaustive-deps
    }, [isEditMode.value]);

    const onSubmit = handleSubmit(async (saveData: studyHubPanelLineSelectorConfigFields) => {
        if (isDirty) {
            saveConfig({
                variables: {
                    studyId: studyRef,
                    studyHubPanelLineSelectorConfig: {
                        id: data?.getStudyHubPanelLineSelectorConfig?.id as string,
                        studyRate: saveData?.studyRate,
                        studySiteMean: saveData?.studySiteMean,
                    },
                },
            })
                .then(async () => {
                    isEditMode.setFalse();
                    ShowSuccess(tRoot('actionsSuccessfulToast'));
                    await refetch();
                })
                .catch(() => {
                    ShowError(t('errorMessage'));
                });
        } else {
            isEditMode.setFalse();
        }
    });

    const isLoading = !data || studyFieldsConfigLoading || isMutatingStudyConfigData;

    return (
        <EditCard
            title={t('title')}
            onEdit={() => isEditMode.setTrue()}
            onCancel={async () => {
                if (componentHasChanges(COMPONENT_NAME)) {
                    const ok = await confirmDiscardChanges();
                    if (!ok) {
                        return;
                    }
                    setComponentHasChanges(COMPONENT_NAME, false);
                }
                reset(defaultValues, { keepDirty: false });
                isEditMode.setFalse();
            }}
            onSave={onSubmit}
            isEditMode={isEditMode.value}
            disabled={!canEdit || isLoading}
            data-testid="panels-config"
        >
            {isLoading && <Loading />}
            {!isLoading && (
                <>
                    <div className={styles.checkboxItem}>
                        <Controller
                            control={control}
                            name="studySiteMean"
                            render={({ field: { onChange, value } }) => (
                                <CoreCheckbox
                                    value={value}
                                    onChange={(nextValue) => {
                                        const checked = nextValue?.value;

                                        onChange(checked);

                                        setValue('studyRate', !checked, { shouldDirty: true });
                                    }}
                                    disabled={!canEdit || isLoading || !isEditMode.value}
                                    label={t('studySiteMean')}
                                    data-testid="checkbox-studySiteMean"
                                />
                            )}
                        />
                    </div>
                    <div className={styles.checkboxItem}>
                        <Controller
                            control={control}
                            name="studyRate"
                            render={({ field: { onChange, value } }) => (
                                <CoreCheckbox
                                    value={value}
                                    onChange={(nextValue) => {
                                        const checked = nextValue?.value;

                                        onChange(checked);

                                        setValue('studySiteMean', !checked, { shouldDirty: true });
                                    }}
                                    disabled={!canEdit || isLoading || !isEditMode.value}
                                    label={t('studyRate')}
                                    data-testid="checkbox-studyRate"
                                />
                            )}
                        />
                    </div>
                </>
            )}
        </EditCard>
    );
};

export default StudyHubPanelLineCalculationSelector;
